import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Banknote } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('eventism_token')}` } });
const usd = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).replace(',', '') : '';

const KIND_LABELS = {
  ticket_earnings: 'Ticket earnings', withdrawal: 'Withdrawal',
  payment: 'Payment sent', transfer: 'Credit sent', adjustment: 'Adjustment',
};

export default function PayoutHistoryPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/organizations/me/wallet/history`, auth())
      .then(r => setData(r.data))
      .catch(e => toast.error(e.response?.data?.detail || 'Could not load payout history'));
  }, []);

  if (!data) return <div className="min-h-screen bg-background pt-28 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-background pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-4 space-y-5" data-testid="payout-history-page">
        <div>
          <h1 className="text-2xl font-extrabold flex items-center gap-2"><Banknote className="h-6 w-6 text-emerald-400" /> Payout History</h1>
          <p className="text-sm text-muted-foreground">{data.org_name ? `${data.org_name} — ` : ''}every wallet movement across all your events.</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <Card><CardContent className="py-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Wallet balance</p>
            <p className="text-3xl font-extrabold" data-testid="ph-balance">{usd(data.balance_cents)}</p>
          </CardContent></Card>
          <Card><CardContent className="py-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Lifetime earned</p>
            <p className="text-3xl font-extrabold text-emerald-400">{usd(data.lifetime_earned_cents)}</p>
          </CardContent></Card>
        </div>

        <div className="rounded-lg border border-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="ph-table">
            <thead><tr className="bg-muted/40">
              <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Event</th><th className="text-left px-3 py-2">Note</th>
              <th className="text-right px-3 py-2">Amount</th>
            </tr></thead>
            <tbody>
              {data.transactions.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-muted-foreground text-xs">No wallet activity yet.</td></tr>
              ) : data.transactions.map(t => (
                <tr key={t.txn_id} className="border-t border-border/60">
                  <td className="px-3 py-1.5 whitespace-nowrap">{fmtDate(t.created_at)}</td>
                  <td className="px-3 py-1.5">{KIND_LABELS[t.kind] || t.kind}</td>
                  <td className="px-3 py-1.5 text-xs">{t.event_name || '—'}</td>
                  <td className="px-3 py-1.5 text-xs text-muted-foreground">{t.note}</td>
                  <td className={`px-3 py-1.5 text-right font-semibold ${t.amount_cents < 0 ? 'text-rose-400' : 'text-emerald-400'}`}>{t.amount_cents < 0 ? '-' : '+'}{usd(Math.abs(t.amount_cents))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
